import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("dq_exit_seen")) return;
    let armed = false;
    const timer = setTimeout(() => (armed = true), 4000);
    const onLeave = (e) => {
      if (armed && e.clientY <= 0) {
        setOpen(true);
        sessionStorage.setItem("dq_exit_seen", "1");
        document.removeEventListener("mouseout", onLeave);
      }
    };
    document.addEventListener("mouseout", onLeave);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onLeave);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      await api.post("/leads", {
        name: "Lead Magnet", email, phone: "-", source: "exit_intent",
        message: "Requested free growth audit via exit intent popup.",
      });
      toast.success("Done! We'll email your free growth audit shortly.");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] grid place-items-center bg-dq-ink/50 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="exit-popup"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white p-8 dq-shadow-soft"
          >
            <button onClick={() => setOpen(false)} data-testid="exit-popup-close"
              aria-label="Close" className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full border border-dq-line text-dq-muted hover:text-dq-ink">
              <X className="h-4 w-4" />
            </button>
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-dq-orange/10 text-dq-orange">
              <Gift className="h-7 w-7" />
            </span>
            <h3 className="mt-5 font-display text-2xl font-extrabold text-dq-ink">
              Wait — grab a free growth audit
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-dq-muted">
              Get a no-obligation audit of your website, SEO and Google profile. See exactly
              where you&rsquo;re losing leads and how to fix it.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com" data-testid="exit-popup-email"
                className="w-full rounded-full border border-dq-line bg-dq-bg px-5 py-3.5 text-sm focus:border-dq-orange focus:outline-none"
              />
              <button type="submit" disabled={loading} data-testid="exit-popup-submit" className="btn-primary w-full disabled:opacity-60">
                {loading ? "Sending..." : "Claim My Free Audit"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
